import toastManager from "../toastManager";

import {
  sanitizePairConfiguration,
  sanitizeImageDetails,
  sanitizeTags,
  appendImagesToFormDataCustomField,
  getValidPairConfiguration,
  sanitizeEntryDataResponseBase,
} from "./commonUtils";

/**
 * This function sanitizes the response from the API for PsoriasisEntry GET data. It takes an object as input,
 * which should contain psoriasis-related properties. If any property is missing or undefined, it
 * assigns a default value to that property. The default values are predefined within the function.
 *
 * The function further processes the properties 'image_one_0_details', 'pair_configuration_0', and 'tags'
 * using the sanitizeImageDetails, sanitizePairConfiguration, and sanitizeTags functions respectively.
 *
 * @function sanitizePsoriasisEntryDataResponse
 * @param {Object} entryDataResponse - An object containing the psoriasis-related properties from the API response.
 * @returns {Object} An object containing the sanitized psoriasis data.
 *
 * @example
 *
 * const apiResponse = {
 *   id: "1",
 *   created: "2021-01-01T00:00:00Z",
 *   date_modified: "2021-01-02T00:00:00Z",
 *   entry_type: "entryType1",
 *   physician_owner: "physician1",
 *   patient_owner: "patient1",
 *   celery_task_id: "task1",
 *   celery_task_status: "status1",
 *   completed_celery_task_id: "completedTask1",
 *   completed_celery_task_success: true,
 *   classification_zc: 0.8,
 *   semantic_zc: 0.6,
 *   erythema: 0.5,
 *   induration: 0.7,
 *   desquamation: 0.6,
 *   overall_affected: 0.55,
 *   original_image: "imageURL",
 *   image_one_0_details: "{\"key\": \"value\"}",
 *   overlay: "overlayURL",
 *   image_two_0_details: "{\"key\": \"value\"}",
 *   pair_configuration_0: "{\"key\": \"value\"}",
 *   thumbnail: "thumbnailURL",
 *   summary: "summary1",
 *   notes: "notes1",
 *   tags: ["tag1", "tag2"],
 * };
 *
 * const sanitizedData = sanitizePsoriasisEntryDataResponse({ entryDataResponse: apiResponse });
 * console.log(sanitizedData);
 *
 */
export const sanitizePsoriasisEntryDataResponse = ({ entryDataResponse }) => {
  const defaultValues = {
    id: "",
    created: "",
    date_modified: "",
    appointment_date: "",
    entry_type: "",
    physician_owner: "",
    patient_owner: "",

    celery_task_id: "",
    celery_task_status: "",
    completed_celery_task_id: "",
    completed_celery_task_success: false,

    classification_zc: 0.5,
    semantic_zc: 0.5,
    erythema: 0,
    induration: 0,
    desquamation: 0,
    overall_affected: 0,

    original_image: "",
    image_one_0_details: "{}",
    overlay: "",
    image_two_0_details: "{}",

    pair_configuration_0: "{}",

    thumbnail: "",
    summary: "",
    notes: "",
    classification_tags: [],
  };

  let sanitizedResponse = sanitizeEntryDataResponseBase({
    entryDataResponse: entryDataResponse,
    defaultValues: defaultValues,
  });

  // Fields which require further sanitization: sanitizeImageDetails, sanitizePairConfiguration,
  // and sanitizeTags functions on the relevant fields.
  sanitizedResponse.image_one_0_details = sanitizeImageDetails(
    sanitizedResponse.image_one_0_details
  );
  sanitizedResponse.image_two_0_details = sanitizeImageDetails(
    sanitizedResponse.image_two_0_details
  );
  sanitizedResponse.pair_configuration_0 = sanitizePairConfiguration(
    sanitizedResponse.pair_configuration_0
  );

  sanitizedResponse.classification_tags = sanitizeTags(
    sanitizedResponse.classification_tags
  );

  return sanitizedResponse;
};

/**
 * This function processes the results of psoriasis calculation data. It takes an object as input,
 * which should contain psoriasis-related properties. If any property is missing, it
 * assigns a default value to that property. The default values are:
 *  - `classification_zc`: 0.5
 *  - `semantic_zc`: 0.5
 *  - `erythema`: 0
 *  - `induration`: 0
 *  - `desquamation`: 0
 *  - `overall_affected`: 0
 *
 * It uses the sanitizeEntryDataResponseBase function to process the input data.
 *
 * After processing the data, it returns an object with the sanitized results where the keys
 * are reformatted into camel case.
 *
 * @function processPsoriasisResults
 * @param {Object} sanitizedEntryDataResponse - An object containing the psoriasis-related properties.
 * @returns {Object} An object containing the processed results with the keys in camel case format.
 *
 * @example
 *
 * const input = {
 *   classification_zc: 0.8,
 *   semantic_zc: 0.6,
 *   erythema: 1,
 *   induration: 2,
 *   desquamation: 1,
 *   overall_affected: 0.55,
 * };
 *
 * const result = processPsoriasisResults({ sanitizedEntryDataResponse: input });
 * console.log(result);
 *
 * // Outputs:
 * // {
 * //   classificationZC: 0.8,
 * //   semanticZC: 0.6,
 * //   erythema: 1,
 * //   induration: 2,
 * //   desquamation: 1,
 * //   overallAffected: 0.55
 * // }
 */
export const processPsoriasisResults = ({ sanitizedEntryDataResponse }) => {
  // Define the default values for the properties
  const defaultValues = {
    // Zoom coefficients
    classification_zc: 0.5,
    semantic_zc: 0.5,
    // Psoriasis data
    erythema: 0,
    induration: 0,
    desquamation: 0,
    overall_affected: 0,
  };

  // Use the sanitizeEntryDataResponseBase function to process the data
  const sanitizedResponse = sanitizeEntryDataResponseBase({
    entryDataResponse: sanitizedEntryDataResponse,
    defaultValues: defaultValues,
  });

  // Return the processed data
  return {
    classificationZC: sanitizedResponse.classification_zc,
    semanticZC: sanitizedResponse.semantic_zc,
    erythema: sanitizedResponse.erythema,
    induration: sanitizedResponse.induration,
    desquamation: sanitizedResponse.desquamation,
    overallAffected: sanitizedResponse.overall_affected,
  };
};

/**
 * A function that prepares FormData for a POST request to perform a calculation.
 * It appends images and their details to the FormData if they exist, along with classification and semantic coefficients.
 * In the case of missing images, it shows a warning message to the user and aborts the processing.
 *
 * @param {Object} params - The parameters for the function.
 * @param {FormData} params.formData - The FormData object to be populated.
 * @param {Array} params.comparisonItems - An array of comparison items containing image files and their details.
 * @param {number} params.classificationZC - The classification zero crossing coefficient to be appended to FormData.
 * @param {number} params.semanticZC - The semantic zero crossing coefficient to be appended to FormData.
 *
 * @returns {boolean} - Returns true if the FormData has been successfully processed, false if images were missing.
 *
 * @throws Will display a toast notification if images for the calculation are missing.
 */
export const processPsoriasisCalculationFormData = ({
  formData,
  comparisonItems,
  classificationZC,
  semanticZC,
}) => {
  const { fileOne, fileOneDetails } = comparisonItems[0];

  // If there are no images in the first comparison item, display a warning and abort the processing
  if (fileOne.length === 0) {
    toastManager(
      "warning",
      "Calculation Warning",
      "Cannot calculate entry with no input images. Add an image to begin the calculation."
    );
    // Return false to indicate an issue during processing
    return false;
  }
  // Populate FormData with the first image and its details
  appendImagesToFormDataCustomField({
    formData: formData,
    fileInput: fileOne,
    fileDetails: fileOneDetails,
    fileName: "original_image",
    detailsName: "image_one_0_details",
  });
  // Append the coefficients to FormData
  formData.append("classification_zc", classificationZC);
  formData.append("semantic_zc", semanticZC);

  // Return true to indicate successful processing
  return true;
};

/**
 * A function that prepares FormData for a POST request to save an entry.
 * It first gets a valid pair configuration, appends it to the FormData, and then appends image details to the FormData.
 * Note that the images themselves are not appended in this case.
 *
 * @param {Object} params - The parameters for the function.
 * @param {FormData} params.formData - The FormData object to be populated.
 * @param {Array} params.comparisonItems - An array of comparison items containing image files and their details.
 *
 * @returns {undefined} - This function does not return a value. It only causes a side effect of populating the provided FormData.
 */
export const processPsoriasisSaveFormData = ({ formData, comparisonItems }) => {
  // Retrieve the valid configuration for the first comparison item
  const validPairConfiguration = getValidPairConfiguration({
    comparisonItem: comparisonItems[0],
  });

  // Append the valid configuration to FormData
  formData.append(
    `pair_configuration_${0}`,
    JSON.stringify(validPairConfiguration)
  );

  // Append the image details to the formData
  const { fileOne, fileTwo, fileOneDetails, fileTwoDetails } =
    comparisonItems[0];

  // Append the details of the first image to FormData without including the image itself
  appendImagesToFormDataCustomField({
    formData: formData,
    fileInput: fileOne,
    fileDetails: fileOneDetails,
    fileName: "original_image",
    detailsName: "image_one_0_details",
    appendImage: false,
  });

  // Append the details of the second image to FormData without including the image itself
  appendImagesToFormDataCustomField({
    formData: formData,
    fileInput: fileTwo,
    fileDetails: fileTwoDetails,
    fileName: "overlay",
    detailsName: "image_two_0_details",
    appendImage: false,
  });
};

/**
 * A function that sanitizes the response from the Celery Psoriasis Calculation Result API call.
 * It checks for missing or undefined fields in the response and provides default values for them.
 * This way, it ensures that the returned object always has a consistent structure.
 *
 * @param {Object} psoriasisCalculationResultResponse - The response from the Celery Psoriasis Calculation Result API call.
 * @param {number} psoriasisCalculationResultResponse.classification_zc - The classification zero crossing coefficient from the response.
 * @param {number} psoriasisCalculationResultResponse.semantic_zc - The semantic zero crossing coefficient from the response.
 * @param {number} psoriasisCalculationResultResponse.erythema - The erythema score from the response.
 * @param {number} psoriasisCalculationResultResponse.induration - The induration score from the response.
 * @param {number} psoriasisCalculationResultResponse.desquamation - The desquamation score from the response.
 * @param {string} psoriasisCalculationResultResponse.overlay - The overlay image URL from the response.
 * @param {number} psoriasisCalculationResultResponse.affected_percentage - The affected skin percentage from the response.
 *
 * @returns {Object} - The sanitized Psoriasis Calculation Result, with all fields ensured to be present and defined.
 */
export const sanitizePsoriasisCalculationResultResponse = ({
  psoriasisCalculationResultResponse = {},
}) => {
  const {
    classification_zc,
    semantic_zc,
    erythema,
    induration,
    desquamation,
    overlay,
    affected_percentage,
  } = psoriasisCalculationResultResponse;

  return {
    classification_zc:
      classification_zc !== undefined ? classification_zc : 0.5,
    semantic_zc: semantic_zc !== undefined ? semantic_zc : 0.5,
    erythema: erythema !== undefined ? erythema : 0,
    induration: induration !== undefined ? induration : 0,
    desquamation: desquamation !== undefined ? desquamation : 0,
    overlay: overlay !== undefined ? overlay : "",
    affected_percentage:
      affected_percentage !== undefined ? affected_percentage : 0,
  };
};
